import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  Users,
  Tag,
  Filter,
  History,
  Star,
  Mail,
  Heart,
  TrendingUp,
  UserPlus,
  Search,
  BarChart3
} from "lucide-react";

export function CRMPage() {
  const features = [
    {
      icon: Users,
      title: "Contact Management",
      description: "Store and organize all customer information in one place. Easy access to contact details, preferences, and history."
    },
    {
      icon: Tag,
      title: "Smart Tagging System",
      description: "Categorize customers with custom tags. Create segments for targeted campaigns and personalized experiences."
    },
    {
      icon: Filter,
      title: "Advanced Segmentation",
      description: "Build complex customer segments based on behavior, demographics, and engagement. Filter and find exactly who you need."
    },
    {
      icon: History,
      title: "Customer History",
      description: "Complete timeline of every interaction. View reviews, loyalty activity, email engagement, and more in one place."
    },
    {
      icon: Star,
      title: "Engagement Tracking",
      description: "Monitor customer engagement levels. Identify your most valuable customers and those at risk of churning."
    },
    {
      icon: TrendingUp,
      title: "Customer Insights",
      description: "Analyze customer lifetime value, purchase patterns, and behavior trends. Understand what drives loyalty."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Import Contacts",
      description: "Add customers manually or import from existing systems",
      icon: UserPlus
    },
    {
      number: 2,
      title: "Organize with Tags",
      description: "Create tags and segments to categorize your customers",
      icon: Tag
    },
    {
      number: 3,
      title: "Track Interactions",
      description: "All customer activities are automatically logged and tracked",
      icon: History
    },
    {
      number: 4,
      title: "Take Action",
      description: "Use segments for targeted campaigns and personalized outreach",
      icon: Star
    }
  ];

  const relatedFeatures = [
    {
      title: "Marketing & Communications",
      description: "Send targeted campaigns to specific customer segments",
      icon: Mail,
      link: "/features/marketing"
    },
    {
      title: "Loyalty & Rewards",
      description: "Reward your best customers with personalized offers",
      icon: Heart,
      link: "/features/loyalty"
    },
    {
      title: "Analytics & Insights",
      description: "Analyze customer data and identify trends",
      icon: BarChart3,
      link: "/features/analytics"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Build Stronger Customer Relationships"
        subtitle="Centralize customer data, track every interaction, and create meaningful segments. Turn customer information into actionable insights."
        primaryCTA={{ text: "Start Managing Contacts", link: "/sign-up" }}
        secondaryCTA={{ text: "Learn More", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Complete Customer Relationship Management"
        subtitle="Everything you need to know and manage your customer base"
      >
        <FeatureGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Organize and leverage your customer data in 4 steps"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="360° Customer View"
          description="See everything about a customer in one place. Their contact information, tag assignments, review history, loyalty program participation, email engagement, and purchase behavior. No more switching between systems to understand your customers."
        />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Powerful Segmentation"
          description="Create sophisticated customer segments with our flexible tagging and filtering system. Target VIP customers, recent reviewers, loyalty program members, or any custom group you define. Use segments across all features for personalized marketing, rewards, and communications."
          imagePosition="left"
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="CRM integrated across your entire platform"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Know Your Customers Better"
          description="Stop losing track of customer information. Build lasting relationships with a CRM that actually works."
          primaryCTA={{ text: "Get Started Free", link: "/sign-up" }}
          secondaryCTA={{ text: "See It In Action", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
